import React from "react";
import styled from "styled-components";
import { colors, radius, shadow } from "./theme";
import AppIcon from "./AppIcon";
import type { AppConfig } from "./types";

const Button = styled.button<{ $dragging: boolean; $selected: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 7px;
  width: 92px;
  padding: 8px 4px 6px;
  border: none;
  background: ${({ $selected, $dragging }) =>
    $selected
      ? "rgba(83, 218, 169, 0.32)"
      : $dragging
      ? "rgba(29, 29, 31, 0.1)"
      : "transparent"};
  box-shadow: ${({ $selected }) =>
    $selected ? `inset 0 0 0 1.5px ${colors.ink}` : "none"};
  border-radius: ${radius.md};
  cursor: ${({ $dragging }) => ($dragging ? "grabbing" : "grab")};
  font-family: inherit;
  user-select: none;
  -webkit-user-select: none;

  &:hover {
    background: ${({ $selected }) =>
      $selected ? "rgba(83, 218, 169, 0.32)" : "rgba(29, 29, 31, 0.06)"};
  }
  &:focus-visible {
    outline: 2px solid ${colors.ink};
  }
`;

const Tile = styled.div<{ $accent: string }>`
  width: 58px;
  height: 58px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: ${radius.md};
  border: 2px solid ${colors.ink};
  background: ${({ $accent }) => $accent};
  box-shadow: ${shadow.icon};
  color: ${colors.ink};
  transition: transform 0.12s ease, box-shadow 0.12s ease;

  ${Button}:hover & {
    transform: translate(-1px, -2px);
    box-shadow: 4px 5px 0 rgba(29, 29, 31, 0.22);
  }
  ${Button}:active & {
    transform: translate(1px, 1px);
    box-shadow: 1px 1px 0 rgba(29, 29, 31, 0.22);
  }
`;

const Label = styled.span`
  font-size: 12.5px;
  font-weight: 600;
  color: ${colors.ink};
  text-align: center;
  line-height: 1.2;
  padding: 1px 6px;
  border-radius: ${radius.sm};
  background: rgba(255, 255, 255, 0.55);
  backdrop-filter: blur(2px);
`;

const DesktopIcon = ({
  app,
  dragging = false,
  selected = false,
}: {
  app: AppConfig;
  dragging?: boolean;
  selected?: boolean;
}) => {
  return (
    <Button
      $dragging={dragging}
      $selected={selected}
      type="button"
      title={`Open ${app.title}`}
      draggable={false}
    >
      <Tile $accent={app.accent}>
        <AppIcon
          icon={app.icon}
          size={28}
          color={app.accent === colors.ink ? "#fff" : colors.ink}
        />
      </Tile>
      <Label>{app.title}</Label>
    </Button>
  );
};

export default DesktopIcon;
